import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Card } from '@orisini/ui/components/card'
import { QueueList } from '../../queue/components/QueueList'
import { accountsApi, type QueueItem, type Workspace } from '../lib/accounts'
import { contentApi, describeEvent, when, type HistoryEvent } from '../../content/lib/content'

/** Ringkasan project: apa yang akan terbit berikutnya (kalau berakun) dan riwayat produksi. */
export function OverviewTab({ workspace: w }: { workspace: Workspace }) {
  const [items, setItems] = useState<QueueItem[] | null>(null)

  const muat = useCallback(() => {
    // Project tanpa akun tidak punya antrean.
    if (!w.productCode) return setItems([])
    accountsApi
      .queue()
      .then((r) => setItems(r.items.filter((i) => i.workspaceId === w.id)))
      .catch(() => setItems([]))
  }, [w.id, w.productCode])

  useEffect(() => muat(), [muat])

  const [history, setHistory] = useState<HistoryEvent[] | null>(null)
  useEffect(() => {
    contentApi.history(w.id).then(setHistory).catch(() => setHistory([]))
  }, [w.id])

  const aktif = items?.filter((i) => i.scheduledAt && !i.onHold) ?? []
  const berikutnya = aktif[0]?.scheduledAt ?? null

  return (
    <div className="space-y-6">
      {!w.productCode && (
        <Card className="p-4 text-sm">
          <span className="font-medium">No account connected. </span>
          <span className="text-muted-foreground">
            What this project makes stays in the{' '}
            <Link to={`/bank?account=${w.id}`} className="text-foreground underline-offset-4 hover:underline">Content Bank</Link>{' '}
            for you to download.{' '}
            <Link to={`/projects/${w.id}/connection`} className="text-foreground underline-offset-4 hover:underline">Connect an account</Link>{' '}
            to have Orisini Agent post it.
          </span>
        </Card>
      )}

      {w.productCode && (<>
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="In the queue" value={items ? String(items.length) : '—'} />
        <Stat label="Scheduled" value={items ? String(aktif.length) : '—'} />
        <Stat
          label="Next post"
          value={
            berikutnya
              ? new Date(berikutnya).toLocaleString('en-GB', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })
              : '—'
          }
        />
      </div>

      {w.statusReason && (
        <Card className="p-4 text-sm">
          <span className="font-medium">{w.status === 'agent_offline' ? 'Agent offline. ' : 'Needs attention. '}</span>
          <span className="text-muted-foreground">{w.statusReason}</span>
        </Card>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Coming up</h2>
          <Link to={`/projects/${w.id}/publishing`} className="text-sm text-muted-foreground hover:text-foreground">
            Posting times
          </Link>
        </div>
        {items === null ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing in the queue for this project.</p>
        ) : (
          <QueueList items={items.slice(0, 10)} onChanged={muat} showAccount={false} />
        )}
      </section>
      </>)}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Production history</h2>
        {history === null ? null : history.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing produced for this project yet.</p>
        ) : (
          <ol className="space-y-1.5 border-l border-border pl-4">
            {history.slice(0, 20).map((e, i) => (
              <li key={i} className="text-sm">
                <span className="text-muted-foreground">{when(e.at)}</span> ·{' '}
                <Link to={`/content/${e.contentId}`} className="hover:underline">{e.title}</Link>{' '}
                <span className="text-muted-foreground">({e.kind}) — {describeEvent(e)}</span>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card className="space-y-1 p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </Card>
  )
}
